import React from 'react';
import { THEMES, setTheme, toggleDark, isDarkTheme, useThemeStore } from '@/lib/themes';
import { useT } from '@/lib/i18n';
import { Moon, Contrast, Check } from 'lucide-react';

// Reusable theme picker. `compact` shows only quick toggles (Dark / High Contrast).
export const ThemeSwitcher = ({ compact = false, onChange }) => {
  const t = useT();
  const current = useThemeStore((s) => s.theme);

  const pick = (id) => {
    setTheme(id);
    onChange && onChange(id);
  };

  if (compact) {
    const darkActive = isDarkTheme(current);
    const contrastActive = current === 'high-contrast';
    return (
      <div className="flex items-center gap-2">
        <button
          data-testid="theme-quick-dark"
          onClick={() => pick(toggleDark(current))}
          aria-pressed={darkActive}
          aria-label="Toggle dark mode for this theme"
          className="inline-flex items-center gap-1.5 rounded-full px-3 min-h-[40px] text-sm font-medium border"
          style={{ background: darkActive ? 'var(--primary)' : 'var(--surface)', color: darkActive ? 'var(--primary-foreground)' : 'var(--text)', borderColor: 'var(--border)' }}
        >
          <Moon size={16} /> {t('a_dark')}
        </button>
        <button
          data-testid="theme-quick-high-contrast"
          onClick={() => pick(contrastActive ? 'sakaysafe-light' : 'high-contrast')}
          aria-pressed={contrastActive}
          aria-label="Toggle high contrast theme"
          className="inline-flex items-center gap-1.5 rounded-full px-3 min-h-[40px] text-sm font-medium border"
          style={{ background: contrastActive ? 'var(--primary)' : 'var(--surface)', color: contrastActive ? 'var(--primary-foreground)' : 'var(--text)', borderColor: 'var(--border)' }}
        >
          <Contrast size={16} /> {t('a_contrast')}
        </button>
      </div>
    );
  }

  const all = [{ id: 'system', label: t('theme_system'), hint: t('theme_system_hint'), swatch: ['#F6FBFA', '#0F766E', '#071A18'] }, ...THEMES];

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="theme-picker-grid">
      {all.map((t) => {
        const active = current === t.id;
        return (
          <button
            key={t.id}
            data-testid={`theme-picker-${t.id}`}
            onClick={() => pick(t.id)}
            className="text-left rounded-2xl p-3 border transition-colors"
            style={{ background: 'var(--surface)', borderColor: active ? 'var(--primary)' : 'var(--border)', boxShadow: active ? 'var(--shadow)' : 'none', outline: active ? '2px solid var(--primary)' : 'none' }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-1">
                {t.swatch.map((c, i) => (
                  <span key={`${t.id}-${i}`} className="w-5 h-5 rounded-full border" style={{ background: c, borderColor: 'var(--border)' }} />
                ))}
              </div>
              {active && <Check size={16} style={{ color: 'var(--primary)' }} />}
            </div>
            <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{t.label}</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.hint}</div>
          </button>
        );
      })}
    </div>
  );
};
